/**
 * Pointing a scanner at a box.
 *
 * WHY IT IS JUST AN INPUT
 * -----------------------
 * A barcode scanner is a keyboard. It types the digits and presses Enter,
 * faster than a person could, and nothing else. So there is no camera to
 * open, no permission to grant and no library to load — a focused text box
 * and an Enter handler is the whole of it, and it works with every scanner
 * anybody is likely to have already bought.
 *
 * WHY A MISS IS AN OFFER RATHER THAN AN ERROR
 * -------------------------------------------
 * There are 604 ingredients and none of them has a code yet. Nobody is going
 * to sit and type 604; they arrive one at a time, scanned at a delivery,
 * attached to whatever the receiver says the box is. A screen that treats an
 * unknown code as a failure makes the ordinary case look broken and never
 * builds the list.
 */
import { useState, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';

import { api } from '../../lib/api.js';
import { Button } from '../../components/ui/Button.jsx';

export function ScanBox({ lines = [], onMatch, onAttached }) {
  const [code, setCode] = useState('');
  const [unknown, setUnknown] = useState(null);
  const [attachTo, setAttachTo] = useState('');
  const inputRef = useRef(null);

  const scan = useMutation({
    mutationFn: (barcode) => api.get(`/menu/ingredients/scan/${encodeURIComponent(barcode)}`),
    onSuccess: (result) => {
      setCode('');
      // Straight back to the box: the next box is already in somebody's hand.
      inputRef.current?.focus();

      if (result.found) {
        setUnknown(null);
        onMatch?.(result.ingredient);
      } else {
        setUnknown(result);
      }
    },
  });

  const attach = useMutation({
    mutationFn: () =>
      api.put(`/menu/ingredients/${attachTo}/barcode`, { barcode: unknown.barcode }),
    onSuccess: (result) => {
      setUnknown(null);
      setAttachTo('');
      inputRef.current?.focus();
      onAttached?.(result.ingredient);
    },
  });

  return (
    <div className="rounded-lg border border-rule bg-surface-3 p-3">
      <label className="block">
        <span className="mb-1.5 block text-xs text-ink-3">Scan a box</span>
        <input
          ref={inputRef}
          value={code}
          onChange={(e) => setCode(e.target.value)}
          onKeyDown={(e) => {
            // A scanner ends with Enter. This is the whole integration.
            if (e.key === 'Enter' && code.trim()) {
              e.preventDefault();
              scan.mutate(code.trim());
            }
          }}
          placeholder="Point the scanner here"
          aria-label="Scan a box"
          className="h-11 w-full rounded-lg border border-rule bg-surface px-3 font-mono text-sm text-ink focus:border-brand-400 focus:outline-none"
        />
      </label>

      {scan.isError && (
        <p className="mt-2 text-sm text-bad">{scan.error?.message ?? 'That did not work.'}</p>
      )}

      {unknown && (
        <div className="mt-3 rounded-lg border border-warn bg-warn-bg p-3">
          <p className="text-sm text-warn">
            {unknown.reason} <span className="font-mono">{unknown.barcode}</span>
          </p>

          {lines.length > 0 ? (
            <>
              <p className="mt-2 text-sm text-ink-2">Which of these is it?</p>

              <div className="mt-2 flex flex-wrap items-center gap-2">
                <select
                  value={attachTo}
                  onChange={(e) => setAttachTo(e.target.value)}
                  aria-label="Which of these is it"
                  className="h-11 min-w-0 flex-1 rounded-lg border border-rule bg-surface px-3 text-sm text-ink focus:border-brand-400 focus:outline-none"
                >
                  <option value="">Choose</option>
                  {lines.map((line) => (
                    <option key={line.ingredient_id} value={line.ingredient_id}>
                      {line.ingredient_name}
                    </option>
                  ))}
                </select>

                <Button onClick={() => attach.mutate()} disabled={!attachTo || attach.isPending}>
                  {attach.isPending ? 'Attaching' : 'That is it'}
                </Button>
                <Button variant="secondary" onClick={() => setUnknown(null)}>
                  Skip
                </Button>
              </div>
            </>
          ) : (
            // Nothing on this delivery to attach it to, so saying "choose one"
            // would be offering an empty list.
            <p className="mt-2 text-sm text-ink-2">
              Nothing on this delivery to attach it to. It can be set on the ingredient itself.
            </p>
          )}

          {attach.isError && (
            <p className="mt-2 text-sm text-bad">{attach.error?.message ?? 'That did not work.'}</p>
          )}
        </div>
      )}
    </div>
  );
}
